import React, { useState, useEffect, useContext } from 'react';
import {
    Link,
    ContentCopy,
    CheckCircle,
    Schedule,
    Receipt,
    WhatsApp,
    Sms,
    Email,
    Person,
    Message,
    Add,
    Search,
    Close
} from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';
import { fetchPaymentLinks } from '../../services/paymentLinkService';
import { useAuth } from '../../contexts/AuthContext';
import { XhrContext } from '../../contexts/XhrContext';
import moment from 'moment';

const PaymentLinkPanel = ({ caissierData, terminal, selectedClient }) => {
    const { selectedOrganization, user } = useAuth();
    const { getRequest } = useContext(XhrContext);
    
    // États pour la liste
    const [paymentLinks, setPaymentLinks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [pge, setPage] = useState(1);
    const [lmt] = useState(10);
    const [sts, setStatus] = useState('all');
    const [slug, setSlug] = useState('');
    const [prevPage, setPrevPage] = useState(1);
    const [nextPage, setNextPage] = useState(1);
    
    // États pour la création
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [amount, setAmount] = useState('');
    const [currency, setCurrency] = useState('USD');
    const [description, setDescription] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [customMessage, setCustomMessage] = useState('');
    const [generatedLink, setGeneratedLink] = useState('');
    const [isCopied, setIsCopied] = useState(false);

    // Charger les liens de paiement (logique du mobile)
    useEffect(() => {
        if (selectedOrganization?.id) {
            loadPaymentLinks();
        }
    }, [selectedOrganization?.id, sts, slug]);

    // Pré-remplir le formulaire si un client est sélectionné
    useEffect(() => {
        if (selectedClient) {
            setPhoneNumber(selectedClient.phone || '');
            setShowCreateForm(true);
        }
    }, [selectedClient]);

    const loadPaymentLinks = async () => {
        try {
            setLoading(true);
            const params = {
                sts: sts,
                org: selectedOrganization.id,
                emt: user?.id || 'all',
                cmr: 'all',
                slg: slug,
                pge: pge,
                lmt: lmt
            };
            
            const data = await fetchPaymentLinks(params);
            
            if (data && data?.contents?.length > 0) {
                setPaymentLinks(data.contents || []);
                setPrevPage(data.previousPage || 1);
                setNextPage(data.nextPage || 1);
            } else {
                setPaymentLinks([]);
                setPrevPage(1);
                setNextPage(1);
                setPage(1);
            }
        } catch (error) {
            console.error('Error loading payment links:', error);
            setPaymentLinks([]);
        } finally {
            setLoading(false);
        }
    };

    const getStatusStyle = (status) => {
        const normalizedStatus = status?.toLowerCase() || '';
        switch (normalizedStatus) {
            case 'paid': return 'bg-green-100 text-green-800 border-green-200';
            case 'pending': return 'bg-amber-100 text-amber-800 border-amber-200';
            case 'cashed': return 'bg-blue-100 text-blue-800 border-blue-200';
            case 'expired': return 'bg-red-100 text-red-800 border-red-200';
            default: return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    const getStatusTranslate = (status) => {
        switch (status) {
            case 'all': return 'Tous';
            case 'paid': return 'Payée';
            case 'pending': return 'En attente';
            case 'cashed': return 'Encaissée';
            case 'expired': return 'Expirée';
            default: return 'En attente';
        }
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return '';
        return moment(dateString).format('DD.MM.YYYY HH:mm').replace(':', 'h');
    };

    const formatCurrency = (value, curr = 'USD') => {
        return new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: curr
        }).format(value);
    };

    const generatePaymentLink = () => {
        if (!amount || amount <= 0) {
            alert('Veuillez entrer un montant valide');
            return;
        }
        // Ici, vous devrez appeler l'API createPaymentLink
        // Pour l'instant, on simule
        const link = `${window.location.origin}/pay/LINK_${Date.now()}`;
        setGeneratedLink(link);
    };

    const copyToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            setIsCopied(true);
            setTimeout(() => setIsCopied(false), 2000);
        } catch (err) {
            console.error('Erreur de copie:', err);
        }
    };

    const resetForm = () => {
        setAmount('');
        setDescription('');
        setPhoneNumber('');
        setCustomMessage('');
        setGeneratedLink('');
        setIsCopied(false);
        setShowCreateForm(false);
    };

    // Si on affiche le formulaire de création
    if (showCreateForm) {
        return (
            <div className="space-y-6">
                <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 p-8">
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center space-x-3">
                            <div className="p-3 bg-gradient-to-r from-purple-500 to-purple-600 rounded-2xl">
                                <Link className="text-white text-2xl" />
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold text-gray-900">Lien de Paiement</h2>
                                <p className="text-gray-600">Créez un lien de paiement valable 2 semaines</p>
                            </div>
                        </div>
                        <button
                            onClick={() => setShowCreateForm(false)}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-xl transition-all"
                        >
                            <Close />
                        </button>
                    </div>

                    <div className="mb-6">
                        <label className="block text-sm font-semibold text-gray-700 mb-3">Montant</label>
                        <div className="flex space-x-3">
                            <select
                                value={currency}
                                onChange={(e) => setCurrency(e.target.value)}
                                className="bg-gray-100 border-0 rounded-2xl px-4 py-3 font-semibold focus:ring-2 focus:ring-purple-500 focus:outline-none"
                            >
                                <option value="USD">USD</option>
                                <option value="CDF">CDF</option>
                            </select>
                            <input
                                type="number"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                placeholder="0.00"
                                className="flex-1 bg-gray-50 border-0 rounded-2xl px-4 py-3 text-2xl font-bold text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:outline-none"
                            />
                        </div>
                    </div>

                    <div className="mb-6">
                        <label className="block text-sm font-semibold text-gray-700 mb-3">Numéro du client *</label>
                        <input
                            type="tel"
                            value={phoneNumber}
                            onChange={(e) => setPhoneNumber(e.target.value)}
                            placeholder="+243 97 123 4567"
                            className="w-full bg-gray-50 border-0 rounded-2xl px-4 py-3 text-gray-700 placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:outline-none"
                        />
                    </div>

                    <div className="mb-6">
                        <label className="block text-sm font-semibold text-gray-700 mb-3">Description</label>
                        <input
                            type="text"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Description du paiement..."
                            className="w-full bg-gray-50 border-0 rounded-2xl px-4 py-3 text-gray-700 placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:outline-none"
                        />
                    </div>

                    <div className="mb-8">
                        <label className="block text-sm font-semibold text-gray-700 mb-3">
                            <span className="flex items-center space-x-2">
                                <Message className="text-gray-400" />
                                <span>Message personnalisé (optionnel)</span>
                            </span>
                        </label>
                        <textarea
                            value={customMessage}
                            onChange={(e) => setCustomMessage(e.target.value)}
                            rows="3"
                            placeholder="Personnalisez le message qui sera envoyé avec le lien..."
                            className="w-full bg-gray-50 border-0 rounded-2xl px-4 py-3 text-gray-700 placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:outline-none resize-none"
                        />
                    </div>

                    <div className="flex space-x-4">
                        <button
                            onClick={generatePaymentLink}
                            disabled={!amount || !phoneNumber}
                            className="flex-1 bg-gradient-to-r from-purple-500 to-purple-600 text-white py-4 px-6 rounded-2xl font-semibold shadow-lg hover:from-purple-600 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Link className="mr-2" />
                            Générer le Lien
                        </button>
                        <button
                            onClick={resetForm}
                            className="px-6 py-4 bg-gray-100 text-gray-700 rounded-2xl font-semibold hover:bg-gray-200 transition-all"
                        >
                            Effacer
                        </button>
                    </div>

                    {/* Aperçu du lien généré */}
                    {generatedLink && (
                        <div className="mt-8 bg-gradient-to-r from-purple-50 to-purple-100 rounded-2xl p-6 border border-purple-200">
                            <div className="text-center mb-6">
                                <QRCodeSVG value={generatedLink} size={150} level="H" className="rounded-lg mx-auto" />
                            </div>
                            <div className="space-y-3 mb-4">
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-600">Montant:</span>
                                    <span className="text-xl font-bold text-purple-600">
                                        {formatCurrency(parseFloat(amount), currency)}
                                    </span>
                                </div>
                            </div>
                            <div className="flex items-center space-x-2 bg-white rounded-xl p-3">
                                <input
                                    type="text"
                                    value={generatedLink}
                                    readOnly
                                    className="flex-1 bg-transparent text-sm text-gray-700 font-mono truncate"
                                />
                                <button
                                    onClick={() => copyToClipboard(generatedLink)}
                                    className="p-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
                                >
                                    {isCopied ? <CheckCircle /> : <ContentCopy />}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // Affichage de la liste (comme dans le mobile)
    return (
        <div className="space-y-6">
            {/* En-tête */}
            <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 p-6">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-3">
                        <div className="p-3 bg-gradient-to-r from-purple-500 to-purple-600 rounded-2xl">
                            <Link className="text-white text-2xl" />
                        </div>
                        <div>
                            <h2 className="text-2xl font-bold text-gray-900">Liens de Paiement</h2>
                            <p className="text-gray-600">{paymentLinks.length} lien(s)</p>
                        </div>
                    </div>
                    <button
                        onClick={() => setShowCreateForm(true)}
                        className="flex items-center space-x-2 bg-gradient-to-r from-purple-500 to-purple-600 text-white px-6 py-3 rounded-2xl font-semibold hover:from-purple-600 hover:to-purple-700 transition-all shadow-lg"
                    >
                        <Add />
                        <span>Nouveau lien</span>
                    </button>
                </div>

                {/* Filtres de statut */}
                <div className="flex flex-wrap gap-2 mb-4">
                    {['all', 'pending', 'paid', 'cashed', 'expired'].map((status) => (
                        <button
                            key={status}
                            onClick={() => setStatus(status)}
                            className={`px-4 py-2 rounded-xl font-medium transition-all ${
                                sts === status
                                    ? 'bg-purple-500 text-white shadow-lg'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                            {getStatusTranslate(status)}
                        </button>
                    ))}
                </div>

                {/* Recherche */}
                <div className="relative mb-6">
                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Rechercher par numéro ou client..."
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setSlug(e.target.value);
                        }}
                        className="w-full pl-12 pr-4 py-3 bg-gray-50 border-0 rounded-2xl text-gray-700 placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:outline-none"
                    />
                </div>
            </div>

            {/* Liste des liens */}
            <div className="space-y-4">
                {loading ? (
                    <div className="text-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-purple-500 mx-auto mb-4"></div>
                        <p className="text-gray-600">Chargement...</p>
                    </div>
                ) : paymentLinks.length === 0 ? (
                    <div className="text-center py-12 bg-white rounded-3xl shadow-xl border border-gray-100">
                        <div className="text-6xl text-gray-300 mb-4">🔗</div>
                        <h3 className="text-lg font-semibold text-gray-500 mb-2">Aucun lien trouvé</h3>
                        <p className="text-gray-400 mb-6">Créez votre premier lien de paiement</p>
                        <button
                            onClick={() => setShowCreateForm(true)}
                            className="inline-flex items-center space-x-2 bg-gradient-to-r from-purple-500 to-purple-600 text-white px-6 py-3 rounded-2xl font-semibold hover:from-purple-600 hover:to-purple-700 transition-all"
                        >
                            <Add />
                            <span>Créer un lien</span>
                        </button>
                    </div>
                ) : (
                    paymentLinks.map((link) => (
                        <div
                            key={link.id}
                            className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all cursor-pointer"
                        >
                            <div className="flex items-center justify-between">
                                <div className="flex-1">
                                    <h3 className="text-lg font-bold text-gray-900 mb-1">
                                        {link.title || 'Lien de paiement'}
                                    </h3>
                                    <p className="text-gray-600 mb-1">
                                        {link.method === 'flash'
                                            ? link?.customer?.name || `${link?.paymentMethod?.prefix || ''}${link?.paymentMethod?.account || ''}`
                                            : link?.customer?.name || 'Client non spécifié'}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                        {formatDateTime(link.create_at || link.createdAt)}
                                    </p>
                                </div>
                                <div className="text-right ml-4">
                                    <div className="text-xl font-bold text-gray-900 mb-2">
                                        {formatCurrency(link.amount, link.currency)}
                                    </div>
                                    <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStatusStyle(link.status)}`}>
                                        {getStatusTranslate(link.status)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Pagination */}
            {paymentLinks.length > 0 && (
                <div className="flex items-center justify-center space-x-4">
                    <button
                        onClick={() => {
                            if (prevPage && prevPage !== pge) {
                                setPage(prevPage);
                            }
                        }}
                        disabled={!prevPage || prevPage === pge}
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Précédent
                    </button>
                    <span className="text-gray-600">Page {pge}</span>
                    <button
                        onClick={() => {
                            if (nextPage && nextPage !== pge) {
                                setPage(nextPage);
                            }
                        }}
                        disabled={!nextPage || nextPage === pge}
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Suivant
                    </button>
                </div>
            )}
        </div>
    );
};

export default PaymentLinkPanel;
